import type {
  LabelColor,
  TicketCol,
  TicketDevice,
  TicketKind,
  TicketPriority,
  TicketType,
} from './api';

/**
 * 카드의 시각 규칙이 전부 여기 있다.
 *
 * **Jira 에서 온 것** — 타입·우선순위·담당자가 1급 속성이고 필터가 이 셋으로
 * 걸린다. **노션에서 온 것** — 카드는 제목이 먼저고, 안 정한 속성은
 * **아무것도 안 그린다.** 회색 「없음」 칩을 그리면 카드마다 빈칸이 늘어서
 * 보드가 시끄러워진다.
 */

export type ColumnDef = {
  id: TicketCol;
  label: string;
  /**
   * 칸 머리를 채우는 색. **상태색 토큰 이름**이다.
   *
   * 회색 → 하늘 → 초록 → 빨강 (2026-08-26 사용자 지정).
   */
  tone: 'gray' | 'blue' | 'amber' | 'green' | 'red' | 'violet';
  /**
   * 옅게 칠하나. **이슈 칸만 그렇다**(2026-08-26 지시 「옅은 회색」).
   *
   * 나머지 셋은 진행 중인 일이라 색이 서고, **접수함은 아직 일이 아니라서**
   * 조용하다. 넷을 똑같이 진하게 칠하면 「무엇이 굴러가고 있나」가 색으로
   * 안 읽힌다.
   */
  soft?: boolean;
};

/**
 * 업무 칸 넷.
 *
 * ⚠ **식별자를 서버와 맞춰라.** `GET /tickets/meta` 가 같은 목록을 주고,
 * 어긋나면 그 칸의 카드가 통째로 사라진다 — 오류 없이.
 */
/* ⚠ **넷 다 옅게 칠한다**(2026-10-18 UI 회의). 진한 단색 머리 넷이 카드보다
   먼저 눈에 들어와 보드가 시끄러웠다. **색(톤)은 2026-08-26 사용자 지정
   그대로다** — 진하기만 낮췄다. */
export const COLUMNS: readonly ColumnDef[] = [
  { id: 'issue', label: '이슈', tone: 'gray', soft: true },
  { id: 'todo', label: 'Todo', tone: 'blue', soft: true },
  { id: 'in_progress', label: '작업 중', tone: 'green', soft: true },
  { id: 'done', label: '완료', tone: 'red', soft: true },
];

/**
 * 칸이 비었을 때 할 말. **넷이 같다**(2026-08-26 지시).
 *
 * 칸마다 다른 문구를 넣었다가 되돌렸다 — 보드를 훑을 때 빈 칸 넷이 서로
 * 다른 말을 하면 읽을 것이 늘기만 하고, 정작 알아야 할 「여기 없다」는
 * 같은 말이다.
 */
export const EMPTY_COLUMN = '티켓 없음';

/**
 * 칸 머리에 얹을 색. **점(pip)을 대신한다**(2026-08-26 지시) — 머리 전체가
 * 색을 말하는데 그 옆에 같은 색 점을 두면 같은 말을 두 번 하는 것이다.
 *
 * **바탕이 진한 쪽(`-f`), 글자가 옅은 쪽(`-t`)** 이다(2026-08-26 지시로
 * 뒤집었다 — 처음에는 반대였고 「원색적으로 더 진하게」를 요청받았다).
 *
 * ⚠ **글자를 흰색으로 박지 마라.** 어두운 테마에서는 `-f` 가 밝은 색이라
 * (`#93c6ff` 등) 흰 글자가 1.7:1 로 무너진다. `-t` 는 그 짝이라 밝은
 * 테마에서는 옅고 어두운 테마에서는 짙어서, **한 줄로 일곱 테마를 다
 * 만족한다** — 실측 5.3~9.9:1.
 */
export function columnHeadVars(def: Pick<ColumnDef, 'tone' | 'soft'>): {
  background: string;
  color: string;
} {
  // 옅은 쪽은 짝을 뒤집는다. 두 조합 다 대비를 실측했다 —
  // 진한 쪽 5.3~9.9:1, 옅은 쪽 5.3~6.1:1.
  return def.soft
    ? { background: `var(--st-${def.tone}-t)`, color: `var(--st-${def.tone}-f)` }
    : { background: `var(--st-${def.tone}-f)`, color: `var(--st-${def.tone}-t)` };
}

export function columnLabel(col: TicketCol): string {
  return COLUMNS.find((item) => item.id === col)?.label ?? col;
}


/**
 * 이슈 타입.
 *
 * ⚠ **2026-08-29 에 아이콘을 걷고 배지로 바꿨다**(지시: 「그 표식은 가독성이
 * 떨어진다」). 옛 판은 `●`·`◆`·`■` 도형 하나였다 — Jira 를 따른 것인데,
 * **Jira 의 아이콘은 배우고 나면 읽히지만 우리 것은 배울 자리가 없었다.**
 * 카드에도 표에도 범례가 없어 도형이 무엇을 뜻하는지 알 길이 `title`
 * 툴팁뿐이었고, 그것은 손가락으로 쓰는 화면에서 아예 안 뜬다.
 *
 * 이제 이름이 그대로 보인다. 색은 그 이름을 거드는 것이지 정보를 지고
 * 있지 않다 — 색각 이상에서도 글자가 남는다.
 */
export type TypeDef = {
  id: TicketType;
  label: string;
  /** 배지 색. 라벨과 같은 여섯 색 토큰을 쓴다 — **자를 두 벌로 안 만든다.** */
  tone: LabelColor;
};

export const TYPES: readonly TypeDef[] = [
  { id: 'bug', label: '버그', tone: 'red' },
  { id: 'feature', label: '기능', tone: 'violet' },
  { id: 'task', label: '작업', tone: 'blue' },
];

export function typeOf(id: TicketType | null): TypeDef | undefined {
  return id ? TYPES.find((item) => item.id === id) : undefined;
}

/**
 * 우선순위.
 *
 * **「보통」을 기본값으로 안 쓴다.** `null` 이 「안 정했다」이고 그때 카드는
 * 아무것도 안 그린다 — 전부 「보통」이 찍혀 있으면 그 칩은 정보가 아니다.
 */
export type PriorityDef = {
  id: TicketPriority;
  label: string;
  /**
   * 위로 갈수록 급하다. **화살표는 남긴다** — 이름 넷은 서로 반대말이
   * 아니라서(「긴급」과 「높음」) 순서가 글자만으로는 안 읽힌다.
   */
  mark: string;
  /** 배지 색. 라벨·유형과 같은 여섯 색 토큰. */
  tone: LabelColor;
};

export const PRIORITIES: readonly PriorityDef[] = [
  { id: 'urgent', label: '긴급', mark: '⇈', tone: 'red' },
  { id: 'high', label: '높음', mark: '↑', tone: 'amber' },
  // 보통·낮음은 회색이다(2026-10-18 UI 회의) — 색은 서둘러야 하는 둘에만 준다.
  { id: 'medium', label: '보통', mark: '→', tone: 'gray' },
  { id: 'low', label: '낮음', mark: '↓', tone: 'gray' },
];

export function priorityOf(id: TicketPriority | null): PriorityDef | undefined {
  return id ? PRIORITIES.find((item) => item.id === id) : undefined;
}

/**
 * 라벨 색.
 *
 * **DB 에는 이름만 있다.** 헥사값을 저장하면 테마 일곱 중 어두운 다섯에서
 * 대비가 깨진다 — 푸는 것은 여기, 값은 `styles.css` 의 상태색 토큰이다.
 *
 * ⚠ **색이 여섯인 것은 토큰이 여섯이라서다.** 늘리려면 테마 일곱 곳에
 * 각각 `-b`·`-f`·`-t` 셋을 더해야 한다 — 자를 두 벌로 만들지 않으려고
 * 라벨 쪽을 토큰에 맞췄다. 서버의 `LABEL_COLORS` 도 같은 여섯이다.
 */
export function labelVars(color: LabelColor): {
  background: string;
  color: string;
  borderColor: string;
} {
  // ⚠ **접미사 뜻을 확인하고 써라.** `-t` 가 옅은 바탕(tint), `-f` 가 진한
  // 글자(foreground), `-b` 가 그 사이 테두리(border)다. 뒤집어 쓰면
  // 「옅은 글자 위 중간 바탕」이 되어 **아무 오류 없이 글자가 안 읽힌다.**
  return {
    background: `var(--st-${color}-t)`,
    color: `var(--st-${color}-f)`,
    borderColor: `var(--st-${color}-b)`,
  };
}

/* ── 2026-08-26 에 더해진 축들 ─────────────────────────────────────────── */

/**
 * 티켓 유형. **만들 때 정하고 안 바뀐다.**
 *
 * ⚠ 칸 이름 `issue` 와 글자가 같지만 다른 축이다 — 유형이 `todo` 인 티켓도
 * 접수함인 `issue` 칸에 설 수 있다.
 */
export const KINDS = [
  { id: 'todo', label: '할 일', icon: '✓' },
  { id: 'issue', label: '이슈', icon: '!' },
] as const;

export function kindLabel(kind: TicketKind): string {
  return KINDS.find((k) => k.id === kind)?.label ?? kind;
}

/** 디바이스. **이슈에만** 쓴다. */
export const DEVICES = [
  { id: 'ios', label: 'iOS' },
  { id: 'android', label: 'Android' },
  { id: 'desktop', label: '데스크톱' },
  { id: 'mobile_web', label: '모바일웹' },
] as const;

/** 환경. 이슈에만 쓴다. develop 은 2026-08-22 폐기라 없다. */
export const ENVIRONMENTS = [
  { id: 'local', label: '로컬' },
  { id: 'staging', label: '스테이징' },
  { id: 'production', label: '프로덕션' },
] as const;

export function deviceLabel(id: TicketDevice | null): string | null {
  return id ? (DEVICES.find((d) => d.id === id)?.label ?? id) : null;
}

export function environmentLabel(id: string | null): string | null {
  return id ? (ENVIRONMENTS.find((e) => e.id === id)?.label ?? id) : null;
}

/**
 * 작업 기간을 한 줄로. **시작일과 예정 일수를 함께 받아야 기간이 된다** —
 * 둘 중 하나만 있으면 날짜 하나로만 말한다.
 *
 * 끝일은 **시작일 + (일수 - 1)** 이다. 「오늘부터 1일」은 오늘 끝나는 것이지
 * 내일 끝나는 것이 아니다.
 */
export function workSpan(startedAt: string | null, days: number | null): string | null {
  if (!startedAt) return null;
  const start = new Date(startedAt);
  const fmt = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  if (!days || days < 1) return fmt(start);
  const end = new Date(start);
  end.setDate(end.getDate() + days - 1);
  return `${fmt(start)} ~ ${fmt(end)} (${days}일)`;
}
